<script setup>
import { selectBraindates } from '#shared/utilities/braindate/select';
import { stripBlockTags } from '@/composables/helpers/html';
import { useBraindateLink } from '@/composables/useBraindateLink';
import { safeBraindateTarget } from '#shared/utilities/braindate/link';
import BraindateCard from './braindate-card.vue';

const props = defineProps({
    headline: { type: String, default: '' },
    copy: { type: String, default: '' },
    buttonLabel: { type: String, default: '' },
    buttonUrl: { type: String, default: '' },
    randomize: { type: Boolean, default: false },
    trending: { type: Boolean, default: false },
    limit: { type: Number, default: 12 },
    year: { type: Number, default: 2026 },
});

// ONE source: the live Braindate feed, synced into D1 (Shannon supplied
// credentials for event 1903 on 2026-08-12) — which is what makes the block
// "dynamically pull in individual Braindates" with nobody touching Builder.
//
// There was a second path here, reading a Builder `braindate` data model as a
// CURATION source that would win when it had records. That model was never
// published: cdn.builder.io answers `404 Model not found` for it in both data
// environments, so getBraindates() failed on EVERY render, logged at error
// level, and fell through to this feed anyway. It was the largest single source
// of error-level log lines on the site and made the homepage look like it was
// failing 44-74% of requests while the actual Bizzabo incident was underway.
//
// Manual curation went with it. The `braindates` prop held Builder RECORD ids,
// which cannot resolve against feed ids (braindate-209018) — selectBraindates
// maps then filters, so a curated list would have rendered an EMPTY block
// rather than a curated one. The block is automated now: order comes from
// randomize/trending, not from a hand-picked list.
const { data: pool } = useAsyncData('braindates-feed', async () => {
    const res = await $fetch('/api/v1/braindates').catch(() => null);
    return res?.braindates || [];
}, { default: () => [] });

// Selection runs inside the computed rather than after mount: useAsyncData
// serializes its SSR result into the payload and the client reuses it, so the
// shuffle produces one stable order with no hydration mismatch.
const cards = computed(() => selectBraindates(pool.value || [], {
    randomize: props.randomize,
    trending: props.trending,
    limit: props.limit,
}));

// Same gate as the card (see braindate-card.vue), but the button has no
// specific Braindate destination — its logged-out path carries no `to`,
// which preserves the existing root-redirect behaviour after login.
//
// The destination is normalised through safeBraindateTarget BEFORE it reaches
// the gate, which is what makes this CTA different from a card. A card's url is
// the whole point of that card, so an off-origin one is a broken entry and
// deliberately gets no SSO. This button always means "go to Braindate", so an
// unauthored, blank or mistyped Builder field resolves to the configured root
// and still signs the visitor in. Normalising here also guarantees the url the
// gate sees is on-origin, so the gate's own no-SSO fallback never fires and the
// SSO pair is always appended for a logged-in visitor.
const { public: { braindateUrl } } = useRuntimeConfig();
const { href: buttonHref, target: buttonTarget, handleClick: handleButtonClick } = useBraindateLink(
    () => safeBraindateTarget(props.buttonUrl, braindateUrl),
    { carryTo: false },
);

const scroller = ref(null);

// One card per press, not 80% of the viewport (UNB-685). The old maths moved a
// variable number of cards depending on window width, and left the track at an
// arbitrary offset so the incoming card was never flush with the copy above it
// (UNB-686). Measuring the first item rather than hardcoding 597 keeps this
// honest at the max-w-[85vw] mobile size, where a card is narrower than its
// authored width — which is also what left the last card half off-screen on a
// phone.
const cardStride = () => {
    const el = scroller.value;
    const first = el?.querySelector('li');
    if (!first) return el?.clientWidth || 0;
    // gap is on the flex container, so it is not part of the item's own box.
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
};

// The CTA renders twice: in the header on desktop, and beside the carousel
// arrows on mobile (UNB-688), which is the arrangement sessions/catalog.vue
// already uses. Shared so the two can never drift apart.
//
// `inline-flex` is deliberately absent — each instance sets its own display.
// Two display utilities in one class list are resolved by CSS source order, not
// by the order they appear in the attribute.
const CTA_CLASS = 'braindates__cta items-center justify-center rounded-[50px] border border-white px-[20px] py-[10px] '
    + 'font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] text-white no-underline whitespace-nowrap '
    + 'transition-colors duration-200 hover:bg-(--magenta) hover:border-transparent';

const scrollBy = (dir) => {
    const el = scroller.value;
    if (!el) return;
    el.scrollBy({ left: dir * cardStride(), behavior: 'smooth' });
};

// Real scroll state for the shared carousel control, which dims and disables an
// arrow on `!hasPrev` / `!hasNext`. Both were previously hardcoded `true`, so
// the arrows always looked live even at either end of the track. Same shape as
// the Agenda calendar's gridCanGoLeft/Right (agenda/calendar-list.vue).
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

// 1px of slack: scrollLeft is fractional on zoomed/hidpi displays, so an exact
// comparison leaves the far arrow enabled at the very end of the track.
const syncScrollState = () => {
    const el = scroller.value;
    if (!el) return;
    canScrollLeft.value = el.scrollLeft > 1;

    // Measured against the last CARD, not scrollWidth. The trailing spacer is a
    // full viewport wide, so raw geometry says there is always more to scroll
    // and the next arrow never disabled — even though snapping correctly holds
    // the last card on the content edge and nothing further is reachable.
    // The spacer is the only li with no child element, which is what separates
    // it from a card here.
    const cards = [...el.querySelectorAll('li')].filter((li) => li.firstElementChild);
    const last = cards[cards.length - 1];
    const scrollPadLeft = parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;
    // The scroll offset that lands the last card on the content edge.
    const maxUseful = last
        ? Math.max(0, last.offsetLeft - scrollPadLeft)
        : el.scrollWidth - el.clientWidth;

    canScrollRight.value = el.scrollLeft < maxUseful - 1;
};

// The cards arrive from useAsyncData, so scrollWidth is 0 on first paint —
// without this the right arrow would start disabled and never recover.
watch(() => cards.value.length, () => nextTick(syncScrollState));

onMounted(() => {
    nextTick(syncScrollState);
    scroller.value?.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);
});

onBeforeUnmount(() => {
    scroller.value?.removeEventListener('scroll', syncScrollState);
    window.removeEventListener('resize', syncScrollState);
});
</script>

<template>
    <!-- Gradient is the comp's own five-stop ramp (4530:105581), not the
         two-token approximation this block shipped with: orange through the
         HubSpot orange, into #af264d and out at #9d1c71. Only the first and
         third stops correspond to tokens (--orange), so it is authored literally
         rather than half in tokens and half in hex.
         90px top/bottom is the comp's py; the block keeps its own background
         because the text renders white, the same way sibling blocks do. -->
    <section
        v-if="cards.length"
        class="block-braindates relative overflow-clip py-[60px] lg:py-[90px]"
        style="background-image: linear-gradient(216.2deg, #ff6a00 0.62%, #d63d00 19.16%, #d63d00 51.84%, #af264d 80.97%, #9d1c71 100%)"
    >
        <!-- UNB-677. Exported from Nick's cleaned-up frame ("Frame 41469",
             5985:58790), which groups the glow ON TOP of the shard. The earlier
             asset came from 5916:187231 at 421x275 — a different crop taken
             before that cleanup — which is why the block kept looking unchanged
             after the first pass.
             Export the LAYERS, not the frame: the frame carries the block's
             orange fill, so exporting it whole produces a fully opaque bitmap
             that paints a rectangle over the gradient.
             421 wide, not the frame's 365: the shard overflows 56px to the left
             and the export expands to include it. The section's overflow-clip
             does the corner crop, exactly as the comp's frame clips it.
             Static public path — never `import` media, it blows the Worker
             memory limit.
             Scaled down below md: at its full 421px it covered 98% of a
             430px viewport and its bright edge washed across the headline and
             the first line of copy (Shannon, Asana 1217708970859504).
             200px, checked on a real 390x844 @3x viewport. Sizing it to fully
             clear the headline's BOX puts it near 130px, which turned out to be
             small enough to read as part of the background gradient rather than
             as the corner treatment. The boxes overlapping is fine: the art's
             opaque area sits top-right with a soft falloff, so the headline
             stays legible under the transparent corner.
             Desktop is untouched — the comp's 421px returns from md (768) up. -->
        <img
            src="/img/2026/braindates/top-right-corner-shard.png"
            srcset="/img/2026/braindates/top-right-corner-shard.png 1x, /img/2026/braindates/top-right-corner-shard@2x.png 2x"
            width="421" height="306"
            loading="lazy" decoding="async"
            alt="" aria-hidden="true"
            class="absolute top-0 right-0 w-[200px] md:w-[421px] h-auto pointer-events-none select-none z-0"
        />

        <!-- UNB-679. Figma "Ellipse 456": a 498px #FF6A00 circle under a 125px
             Gaussian blur, which the export bakes into a 998px SVG box (498 +
             125 of bleed each side). Kept as the exported SVG rather than
             rebuilt as a radial-gradient so the falloff matches the comp
             exactly — it is 675 bytes.
             Placement is derived, not eyeballed: in the 1440x716 comp frame the
             circle centres at (1158, 891), i.e. 282px in from the right edge and
             175px BELOW the block's bottom. Offsetting the 998px box by half its
             size off those two anchors puts the centre where the comp has it.
             Anchored bottom/right because the block's height grows with its
             content while the comp's does not. -->
        <img
            src="/img/2026/braindates/bottom-glow.svg"
            width="998" height="998"
            loading="lazy" decoding="async"
            alt="" aria-hidden="true"
            class="absolute right-[-217px] bottom-[-674px] w-[998px] h-[998px] max-w-none pointer-events-none select-none z-0"
        />

        <!-- Above the shard: without a stacking context the headline and cards
             sit under the artwork. -->
        <div class="relative z-[1] px-(--box-padding-lr-m) lg:px-(--box-padding-lr)">
            <!-- Comp spacing (4530:105586/105587): 40px between the headline and
                 the button, 18px down to the copy, 62px from the copy to the
                 cards. Mobile keeps a tighter stack. -->
            <div class="flex flex-col lg:flex-row lg:items-center gap-[24px] lg:gap-[40px] mb-[18px]">
                <MessHtml
                    v-if="headline"
                    tag="h2"
                    class="font-hubspot-serif font-normal text-[42px] lg:text-[60px] leading-[1.1] text-white mb-0"
                    :html="stripBlockTags(headline)"
                />
                <!-- Utility/Button type style: 17px / 1.4 / 0.34px. The line-height
                     was unset and inheriting, which made the button 47px instead of
                     the comp's ~46 (44px instance plus its 1px border each side).
                     lg:mt-[7px] reproduces the comp's offset: Figma centres the row
                     but wraps the button in a 7px top padding, so its centre lands
                     3.5px BELOW the row centre. In an items-center row a 7px top
                     margin shifts the border box by exactly half that, because
                     centring works on the margin box. -->
                <!-- New tab, same as the cards (see braindate-card.vue): the CTA
                     leaves for Braindate, and the attendee should keep this page. -->
                <!-- Gated on the label alone: the destination no longer depends on
                     the Builder url field, so requiring it would hide the button
                     for no reason. Clear the label to hide the CTA. -->
                <a
                    v-if="buttonLabel"
                    :href="buttonHref"
                    :target="buttonTarget"
                    data-test="braindates-cta-desktop"
                    :class="[CTA_CLASS, 'hidden lg:inline-flex lg:mt-[7px]']"
                    @click="handleButtonClick"
                >{{ buttonLabel }}<span v-if="buttonTarget" class="sr-only"> (opens in a new tab)</span></a>
            </div>

            <div v-if="copy" class="braindates__copy font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] text-white max-w-[785px] mb-[40px] lg:mb-[62px]" v-html="copy"></div>

            <!-- The bleed matches the 20px CARD GAP, not the container gutter.
                 A sliver of the previous card was showing to the left of the
                 focused one (Nick, 2026-08-19): with the track pulled out by the
                 full gutter, a snapped card sits at the content edge while its
                 predecessor ends one gap earlier — so gutter minus gap of it was
                 left visible, 4px on mobile and 20px on desktop. Bleeding by
                 exactly the gap clips it at the track edge instead. Zero is what
                 sessions/catalog.vue gets for free, because its gap and gutter
                 happen to be the same value.

                 The content still starts at the container's padding: the track is
                 pulled out 20px and pads 20px back, so the first card lines up
                 with the headline above it.

                 The track bleeds off BOTH edges rather than ending at the
                 container's padding, so a card leaving the viewport is cut by the
                 viewport itself instead of by an invisible rule 40px in — which is
                 what made a scrolled card look sliced with a band of background
                 beside it.
                 The negative margins cancel the wrapper's padding so the scroll
                 box spans the full width; the matching padding puts that gutter
                 back INSIDE the scroller, so the first card still starts on the
                 content edge at rest and the last one clears the edge at the end.
                 scroll-padding-left keeps that left gutter honoured when the
                 arrows scroll programmatically, so a card lands on the content
                 edge rather than flush against the viewport.
                 Directional resets (my/py) rather than `m-0 p-0`: the shorthands
                 and these directional utilities set the same properties, and
                 relying on class order to settle that is exactly the collision
                 this project keeps getting bitten by. -->
            <!-- snap-x/snap-mandatory + snap-start on each item, the same pattern
                 as sessions/catalog.vue and the featured carousels Nick compared
                 this against (UNB-687). It also settles UNB-686 and the mobile
                 last-card report: a snapped card lands on the scroll-padding
                 edge, which is the content edge, so free-form drift can't leave
                 one half off-screen. -->
            <!-- The vertical padding is what lets the card's hover shadow render.
                 `overflow-x: auto` forces the cross axis to clip, so the shadow
                 was cut flat top and bottom while staying visible left and right
                 (Nick, 2026-08-19). The card's shadow is 0 10px 30px, so it needs
                 ~20px above and ~40px below; the matching negative margins cancel
                 the space again so nothing moves. Same technique as
                 sessions/related.vue. -->
            <ul ref="scroller" class="braindates__track flex gap-[20px] overflow-x-auto snap-x snap-mandatory list-none
                pt-[20px] pb-[40px] -mt-[20px] -mb-[40px]
                -mx-[20px] px-[20px] scroll-pl-[20px]">
                <!-- `flex` on the item, not just h-full on the card: the li already
                     stretches to the tallest card, but that stretched height is not
                     definite, so the card's height:100% resolved to auto and titles
                     of different lengths left the cards (and their Book Your Spot
                     links) at ragged heights. As a flex parent the card stretches
                     outright, and its mt-auto pins the CTA to a shared baseline. -->
                <!-- 597px is the comp's card width (4530:105594), not the 420px this
                     shipped with. max-w keeps it from overflowing a phone, where a
                     597px card would be wider than the viewport. -->
                <li v-for="card in cards" :key="card.id" class="shrink-0 snap-start flex w-[597px] max-w-[85vw]">
                    <BraindateCard :braindate="card" :year="year" />
                </li>

                <!-- Trailing spacer so the LAST card can still snap to the
                     content edge. Same pattern as sessions/speakers.vue.
                     Without it the track runs out of scroll range before the
                     final card reaches its snap point, so it settles wherever
                     max-scroll leaves it — measured 11px off the content edge
                     at 390px, which is Nick's mobile report.
                     A full viewport width (Jack, 2026-08-18) rather than the
                     exact leftover: it guarantees the range at every breakpoint
                     instead of depending on a calc that has to track both the
                     597px and 85vw card sizes. It carries NO snap-start, so
                     mandatory snapping still settles on the last card rather
                     than in the blank.
                     aria-hidden + no content: it is layout, not a list item
                     anyone should hear. -->
                <li
                    v-if="cards.length > 1"
                    aria-hidden="true"
                    class="shrink-0 w-screen"
                />
            </ul>

            <!-- Real API, per agenda/calendar-list.vue:673 — the event is `prev`,
                 not `previous`, and both has-* props are required for the
                 buttons to enable. -->
            <!-- UNB-691: a flat 62px left a conspicuous gap under the cards on
                 mobile. Matches Featured Sessions' mobile value (30px, see
                 sessions/catalog.vue); desktop keeps its own 62px. -->
            <div data-test="braindates-controls-row" class="mt-[30px] lg:mt-[62px] flex items-center gap-[16px]">
                <FragmentsCarouselControls
                    :has-prev="canScrollLeft"
                    :has-next="canScrollRight"
                    variant="pink"
                    prev-label="Previous braindates"
                    next-label="Next braindates"
                    @prev="scrollBy(-1)"
                    @next="scrollBy(1)"
                />
                <!-- Mobile only; the header carries it from lg up. -->
                <a
                    v-if="buttonLabel"
                    :href="buttonHref"
                    :target="buttonTarget"
                    data-test="braindates-cta-mobile"
                    :class="[CTA_CLASS, 'inline-flex lg:hidden']"
                    @click="handleButtonClick"
                >{{ buttonLabel }}<span v-if="buttonTarget" class="sr-only"> (opens in a new tab)</span></a>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* The track is a real overflow-x scroller, so on any platform that paints a
   persistent scrollbar it drew one across the block (UNB-676). Same treatment
   as sessions/catalog.vue — the arrows and drag are the interface here.
   Scoped is safe: .braindates__track is this component's own element, not a
   child component's internals. */
.braindates__track {
    scrollbar-width: none;
}

.braindates__track::-webkit-scrollbar {
    display: none;
}
</style>
